import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Skull, Rocket, SealWarning, Sparkle, FilmSlate } from "@phosphor-icons/react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent } from "@/components/ui/dropdown-menu";
import { fetchNotifications, markNotificationRead, markAllNotificationsRead } from "@/lib/api";

const timeAgo = (iso) => {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const navigate = useNavigate();

  const load = () => {
    fetchNotifications()
      .then((data) => {
        setNotifications(data.notifications);
        setUnreadCount(data.unread_count);
      })
      .catch(() => {});
  };

  useEffect(() => {
    load();
    const interval = setInterval(load, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleItemClick = async (n) => {
    if (!n.read) {
      try {
        await markNotificationRead(n.id);
        setUnreadCount((c) => Math.max(0, c - 1));
        setNotifications((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      } catch {
        // ignore
      }
    }
    if (n.app_id) navigate("/dashboard", { state: { openAppId: n.app_id, openTab: n.type === "render_ready" ? "renders" : "publish" } });
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      setUnreadCount(0);
      setNotifications((prev) => prev.map((x) => ({ ...x, read: true })));
    } catch {
      // ignore
    }
  };

  return (
    <DropdownMenu onOpenChange={(open) => open && load()}>
      <DropdownMenuTrigger asChild>
        <button data-testid="notification-bell-btn" className="relative w-9 h-9 rounded-full flex items-center justify-center bg-white/5 border border-white/10 hover:border-white transition-colors duration-300">
          <Bell size={16} />
          {unreadCount > 0 && (
            <span data-testid="notification-unread-badge" className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-[#F97316] text-[10px] font-bold flex items-center justify-center">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-[#0F0F0F] border-white/10 text-white w-80 p-0">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <p className="text-xs font-bold uppercase tracking-wider text-[#7C93B8]">Notifications</p>
          {unreadCount > 0 && (
            <button data-testid="notification-mark-all-read-btn" onClick={handleMarkAllRead} className="text-[11px] text-white/40 hover:text-white transition-colors duration-300">
              Mark all read
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {notifications.length === 0 ? (
            <p className="text-xs text-white/30 px-4 py-6 text-center">No notifications yet</p>
          ) : (
            notifications.map((n) => (
              <button
                key={n.id}
                type="button"
                data-testid={`notification-item-${n.id}`}
                onClick={() => handleItemClick(n)}
                className={`w-full text-left flex items-start gap-2.5 px-4 py-3 border-b border-white/5 hover:bg-white/5 transition-colors duration-300 ${!n.read ? "bg-white/[0.03]" : ""}`}
              >
                {n.type === "kill_risk" ? (
                  <Skull size={14} className="text-red-400 mt-0.5 shrink-0" />
                ) : n.type === "spend_cap_hit" ? (
                  <SealWarning size={14} className="text-red-400 mt-0.5 shrink-0" />
                ) : n.type === "auto_regenerated" ? (
                  <Sparkle size={14} className="text-[#3B82F6] mt-0.5 shrink-0" />
                ) : n.type === "render_ready" ? (
                  <FilmSlate size={14} className="text-[#34D399] mt-0.5 shrink-0" />
                ) : (
                  <Rocket size={14} className="text-[#F97316] mt-0.5 shrink-0" />
                )}
                <div className="flex-1">
                  <p className={`text-xs leading-relaxed ${n.read ? "text-white/50" : "text-white/90"}`}>{n.message}</p>
                  <p className="text-[10px] text-white/30 mt-1">{timeAgo(n.created_at)}</p>
                </div>
                {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-[#F97316] shrink-0 mt-1.5" />}
              </button>
            ))
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationBell;
